import { useMemo, useState, type CSSProperties, type FC } from 'react';
import type {
  ProjectMemberProfile,
  ProjectMemberWorkItem,
  WorkItemTabKey,
} from '../types';

interface ProjectMemberRequestsOverlayProps {
  isOpen: boolean;
  profile: ProjectMemberProfile | null;
  workItems: ProjectMemberWorkItem[];
  onClose: () => void;
  onOpenCreateModal?: () => void;
  onUpdateStatus: (itemId: string, status: string) => Promise<void>;
  onAddDecision: (itemId: string, statusAfter: string, decisionText: string) => Promise<void>;
  onAddResult: (itemId: string, title: string, fileName?: string) => Promise<void>;
}

function statusAccent(status: string): string {
  switch (status) {
    case 'В работе':
      return '#49ddb7';
    case 'Требует уточнения':
      return '#e2ad3a';
    case 'На согласовании':
      return '#41d2ff';
    case 'Завершено':
      return '#8faebb';
    default:
      return '#ff7b65';
  }
}

/** Requests registry — cab-overlay chrome + cabinet-* body. */
export const ProjectMemberRequestsOverlay: FC<ProjectMemberRequestsOverlayProps> = ({
  isOpen,
  profile,
  workItems,
  onClose,
  onOpenCreateModal,
  onUpdateStatus,
  onAddDecision,
  onAddResult,
}) => {
  const [activeTab, setActiveTab] = useState<WorkItemTabKey>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<ProjectMemberWorkItem | null>(null);
  const [decisionText, setDecisionText] = useState('');
  const [resultTitle, setResultTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredItems = useMemo(() => {
    return workItems.filter((item) => {
      if (activeTab === 'ASSIGNED' && item.kind !== 'ASSIGNED') return false;
      if (activeTab === 'OWN' && item.kind !== 'OWN') return false;
      if (activeTab === 'IN_PROGRESS' && item.status !== 'В работе' && item.status !== 'Требует уточнения') return false;
      if (activeTab === 'REVIEW' && item.status !== 'На согласовании') return false;
      if (activeTab === 'COMPLETED' && item.status !== 'Завершено') return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = item.title.toLowerCase().includes(q);
        const matchesCode = item.item_code.toLowerCase().includes(q);
        const matchesProject =
          item.project_code?.toLowerCase().includes(q) ||
          item.project_title?.toLowerCase().includes(q);
        if (!matchesTitle && !matchesCode && !matchesProject) return false;
      }
      return true;
    });
  }, [workItems, activeTab, searchQuery]);

  if (!isOpen) return null;

  const handleDecisionSubmit = async () => {
    if (!selectedItem || !decisionText.trim()) return;
    setIsSubmitting(true);
    try {
      await onAddDecision(selectedItem.id, 'Завершено', decisionText.trim());
      setDecisionText('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultSubmit = async () => {
    if (!selectedItem || !resultTitle.trim()) return;
    setIsSubmitting(true);
    try {
      await onAddResult(
        selectedItem.id,
        resultTitle.trim(),
        `${resultTitle.trim().replace(/\s+/g, '_')}.docx`,
      );
      setResultTitle('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Заявки</small>
              <h2>Мои заявки</h2>
              <p>
                {profile?.full_name || 'Участник проекта'}
                {profile?.user_code ? ` · ${profile.user_code}` : ''}
              </p>
            </div>
          </div>
          <div className="cab-top-actions">
            {onOpenCreateModal ? (
              <button type="button" className="cab-btn primary" onClick={onOpenCreateModal}>
                Новая заявка
              </button>
            ) : null}
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cabinet-toolbar" style={{ padding: '12px 18px' }}>
          <div className="cabinet-filters">
            {[
              { key: 'ALL', label: 'Все' },
              { key: 'ASSIGNED', label: 'Назначенные' },
              { key: 'OWN', label: 'Собственные' },
              { key: 'IN_PROGRESS', label: 'В работе' },
              { key: 'REVIEW', label: 'На согласовании' },
              { key: 'COMPLETED', label: 'Завершены' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`cabinet-filter${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key as WorkItemTabKey)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            className="cabinet-search"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по коду, названию, проекту…"
          />
        </div>

        <div className="cab-body">
          <section className="cabinet-hero" style={{ '--accent': '#49ddb7' } as CSSProperties}>
            <div>
              <div className="cabinet-eyebrow">Реестр заявок</div>
              <h2>{workItems.length} заявок</h2>
              <p>
                Единый реестр назначенных поручений и авторских обращений с сохранением истории
                изменений.
              </p>
            </div>
          </section>

          {filteredItems.length === 0 ? (
            <div className="cabinet-note" style={{ marginTop: 14 }}>
              Заявок пока нет. Созданная локальная заявка или назначенное поручение появится здесь
              после сохранения.
            </div>
          ) : (
            <div className="cabinet-request-list" style={{ marginTop: 14 }}>
              {filteredItems.map((item) => (
                <article
                  key={item.id}
                  className="cabinet-card"
                  style={
                    {
                      '--accent': selectedItem?.id === item.id ? '#49ddb7' : '#41d2ff',
                      cursor: 'pointer',
                      marginBottom: 10,
                    } as CSSProperties
                  }
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="cabinet-actions" style={{ marginBottom: 8 }}>
                    <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                      {item.item_code}
                    </span>
                    <span className="cabinet-badge" style={{ '--badge': '#8faebb' } as CSSProperties}>
                      {item.kind === 'ASSIGNED' ? 'Поручение' : 'Авторская'}
                    </span>
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': statusAccent(item.status) } as CSSProperties}
                    >
                      {item.status}
                    </span>
                    <span className="cabinet-spacer" />
                    {item.due_date ? (
                      <span style={{ fontSize: 12, color: '#e2ad3a' }}>Срок: {item.due_date}</span>
                    ) : null}
                    {item.project_code ? (
                      <span style={{ fontSize: 12, color: '#8faebb' }}>{item.project_code}</span>
                    ) : null}
                  </div>
                  <strong>{item.title}</strong>
                  {item.description ? (
                    <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
                      {item.description}
                    </p>
                  ) : null}
                  <div className="cabinet-actions" style={{ marginTop: 10 }}>
                    <span style={{ fontSize: 11, color: '#8faebb' }}>
                      Назначил: {item.assigned_by || 'Координатор'} · Исполнитель:{' '}
                      {item.assigned_to || profile?.full_name || 'Участник'}
                    </span>
                    <span className="cabinet-spacer" />
                    <span style={{ fontSize: 12, color: '#49ddb7', fontWeight: 700 }}>
                      Детали и решения →
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedItem ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 760 }}>
            <header className="cabinet-modal-head">
              <div>
                <div className="cabinet-actions">
                  <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                    {selectedItem.item_code}
                  </span>
                  <span
                    className="cabinet-badge"
                    style={{ '--badge': statusAccent(selectedItem.status) } as CSSProperties}
                  >
                    {selectedItem.status}
                  </span>
                </div>
                <div className="cabinet-filters" style={{ marginTop: 8 }}>
                  {['В работе', 'На согласовании', 'Завершено'].map((st) => (
                    <button
                      key={st}
                      type="button"
                      className={`cabinet-filter${selectedItem.status === st ? ' active' : ''}`}
                      onClick={() => {
                        void onUpdateStatus(selectedItem.id, st).then(() => {
                          setSelectedItem({ ...selectedItem, status: st });
                        });
                      }}
                    >
                      {st}
                    </button>
                  ))}
                </div>
                <h2>{selectedItem.title}</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setSelectedItem(null)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </header>

            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                {selectedItem.description ? (
                  <div className="cabinet-note">
                    <strong style={{ display: 'block', marginBottom: 6 }}>Описание и цель</strong>
                    {selectedItem.description}
                  </div>
                ) : null}

                <div className="cabinet-grid2">
                  <article className="cabinet-card" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                    <small style={{ color: '#8faebb' }}>Проект</small>
                    <strong style={{ display: 'block', marginTop: 4 }}>
                      {selectedItem.project_title || selectedItem.project_code || '—'}
                    </strong>
                  </article>
                  <article className="cabinet-card" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
                    <small style={{ color: '#8faebb' }}>Срок выполнения</small>
                    <strong style={{ display: 'block', marginTop: 4, color: '#e2ad3a' }}>
                      {selectedItem.due_date || 'Не установлен'}
                    </strong>
                  </article>
                </div>

                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>
                    История решений и этапов ({selectedItem.decisions.length})
                  </h4>
                  {selectedItem.decisions.length === 0 ? (
                    <div className="cabinet-note">Решений пока не зафиксировано.</div>
                  ) : (
                    <div className="cabinet-stack">
                      {selectedItem.decisions.map((dec) => (
                        <article
                          key={dec.id}
                          className="cabinet-card"
                          style={{ '--accent': '#49ddb7' } as CSSProperties}
                        >
                          <div className="cabinet-actions">
                            <strong>
                              {dec.author_name} ({dec.author_role || 'Роль'})
                            </strong>
                            <span
                              className="cabinet-badge"
                              style={{ '--badge': '#49ddb7' } as CSSProperties}
                            >
                              {dec.status_after}
                            </span>
                          </div>
                          <p style={{ margin: '6px 0 0', fontSize: 12 }}>{dec.decision_text}</p>
                        </article>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>
                    Прикреплённые результаты ({selectedItem.results.length})
                  </h4>
                  {selectedItem.results.length === 0 ? (
                    <div className="cabinet-note">Результатов пока не прикреплено.</div>
                  ) : (
                    <div className="cabinet-stack">
                      {selectedItem.results.map((res) => (
                        <article
                          key={res.id}
                          className="cabinet-card"
                          style={{ '--accent': '#41d2ff' } as CSSProperties}
                        >
                          <div className="cabinet-actions">
                            <strong>{res.title}</strong>
                            {res.file_name ? (
                              <span
                                className="cabinet-badge"
                                style={{ '--badge': '#49ddb7' } as CSSProperties}
                              >
                                {res.file_name}
                              </span>
                            ) : null}
                          </div>
                          {res.notes ? (
                            <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                              {res.notes}
                            </p>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  )}
                </div>

                <div className="cabinet-card" style={{ '--accent': '#49ddb7' } as CSSProperties}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>
                    Добавить решение / подтверждение
                  </h4>
                  <textarea
                    className="cabinet-textarea"
                    value={decisionText}
                    onChange={(e) => setDecisionText(e.target.value)}
                    placeholder="Введите текст решения или замечания…"
                    rows={2}
                  />
                  <div className="cabinet-actions" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="cabinet-btn primary"
                      disabled={isSubmitting || !decisionText.trim()}
                      onClick={() => void handleDecisionSubmit()}
                    >
                      Утвердить решение
                    </button>
                  </div>
                </div>

                <div className="cabinet-card" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>Прикрепить результат работы</h4>
                  <div className="cabinet-actions">
                    <input
                      className="cabinet-input"
                      style={{ flex: 1 }}
                      type="text"
                      value={resultTitle}
                      onChange={(e) => setResultTitle(e.target.value)}
                      placeholder="Название результата (например: Вычитка аннотации_v2)…"
                    />
                    <button
                      type="button"
                      className="cabinet-btn"
                      disabled={isSubmitting || !resultTitle.trim()}
                      onClick={() => void handleResultSubmit()}
                    >
                      Прикрепить файл
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
